import type { AiChatNode } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import type { PickType } from "@intelligems/ig-types/utils";
import type { UiMessageChunkDto } from "@intelligems/ig-types/schemas/ai/ui-message-chunk-dto";

import { UiMessageChunkTypeGuardProcessor } from "./ui-message-chunk-type-guard-processor";
import { UiMessagePartTypeGuardProcessor } from "./ui-message-part-type-guard-processor";
import {
  getLastMessagePartFromMessageId,
  getNodeMessageById,
  getOrCreateNodeMessageById,
  getPartByIdAndMessageId,
} from "./utils";

export class ReasoningProcessor {
  static isReasoningPart(
    part: UiMessageChunkDto
  ): part is
    | PickType<UiMessageChunkDto, "reasoning-start">
    | PickType<UiMessageChunkDto, "reasoning-delta">
    | PickType<UiMessageChunkDto, "reasoning-end"> {
    return (
      UiMessageChunkTypeGuardProcessor.isReasoningStartChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isDeprecatedReasoningChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isReasoningDeltaChunk(part) ||
      UiMessageChunkTypeGuardProcessor.isReasoningEndChunk(part)
    );
  }

  static processStreamPart(
    node: AiChatNode,
    messageId: string,
    streamPart: UiMessageChunkDto
  ): string | undefined {
    if (UiMessageChunkTypeGuardProcessor.isReasoningStartChunk(streamPart)) {
      this.reasoningStart(node, messageId, streamPart);
      return "";
    } else if (
      UiMessageChunkTypeGuardProcessor.isReasoningDeltaChunk(streamPart)
    ) {
      return this.concatReasoningDelta(node, messageId, streamPart);
    } else if (
      UiMessageChunkTypeGuardProcessor.isDeprecatedReasoningChunk(streamPart)
    ) {
      return this.concatDeprecatedReasoning(node, messageId, streamPart);
    } else if (
      UiMessageChunkTypeGuardProcessor.isReasoningEndChunk(streamPart)
    ) {
      this.reasoningEnd(node, messageId, streamPart);
      const lastPart = getLastMessagePartFromMessageId(
        node.messages,
        messageId
      );
      if (
        lastPart &&
        UiMessagePartTypeGuardProcessor.isReasoningPart(lastPart) &&
        lastPart.state === "done"
      ) {
        return lastPart.text;
      }
    }
    return undefined;
  }

  private static reasoningStart(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "reasoning-start">
  ) {
    const existingPart = getPartByIdAndMessageId(
      node.messages,
      messageId,
      streamPart.id
    );
    if (existingPart) {
      return;
    }

    const message = getOrCreateNodeMessageById(
      node,
      messageId,
      "assistant",
      streamPart.timestamp
    );

    message.parts.push({
      id: streamPart.id,
      type: "reasoning",
      text: "Thinking...",
      state: "streaming",
      timestamp: streamPart.timestamp,
    });
  }

  private static concatReasoningDelta(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "reasoning-delta">
  ): string {
    const part = getPartByIdAndMessageId(
      node.messages,
      messageId,
      streamPart.id
    );

    if (part && UiMessagePartTypeGuardProcessor.isReasoningPart(part)) {
      if (part.text === "Thinking...") {
        part.text = "";
      }

      part.text += streamPart.delta;

      // part.text = part.text.replaceAll(/([^\n])\n([^\n])/g, "$1\n\n$2");
      part.state = "streaming";
      part.timestamp = streamPart.timestamp;

      if (
        node.type !== "condition" &&
        part.text.toLowerCase().includes("evaluating") &&
        part.text.toLowerCase().includes("condition")
      ) {
        node.type = "condition";
      }

      return part.text;
    }
    return "";
  }

  private static concatDeprecatedReasoning(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "reasoning">
  ): string {
    const id = `deprecated-reasoning`;
    let part = getPartByIdAndMessageId(node.messages, messageId, id);

    if (!part) {
      this.reasoningStart(node, messageId, {
        type: "reasoning-start",
        id: id,
        nodeName: node.name,
        nodeId: node.id,
        nodeType: "other",
        timestamp: streamPart.timestamp,
      });
      part = getPartByIdAndMessageId(node.messages, messageId, id);
    }

    if (part && UiMessagePartTypeGuardProcessor.hasText(part)) {
      if (part.text !== "Thinking...") {
        part.text += streamPart.text;
      }
      part.state = "streaming";
      part.timestamp = streamPart.timestamp;

      return part.text;
    }

    return "";
  }

  private static reasoningEnd(
    node: AiChatNode,
    messageId: string,
    streamPart: PickType<UiMessageChunkDto, "reasoning-end">
  ) {
    const part = getPartByIdAndMessageId(
      node.messages,
      messageId,
      streamPart.id
    );

    if (part && UiMessagePartTypeGuardProcessor.isReasoningPart(part)) {
      part.state = "done";
      part.timestamp = streamPart.timestamp;
    }
  }
}
